import { forwardRef, memo, useEffect } from 'react';
import { loadCouponHistoryV2Request, removeCreatedOpenedCoupons, selectCouponLimitsLoaded, selectCouponSelectedBetsV2, selectCouponSuggestedAmountsLoaded, selectCreatedCouponsV2, selectFreebetsIsFirstLoaded, selectorCouponHistoryOpenCouponsV2, selectUserBalanceIsFirstLoaded, selectUserCurrency, selectUserIsAuthenticated, selectUserLocale, usePrevious } from '@mwl/core-lib';
import ArrowLeft from '@public/assets/common/icons/arrowLeft.svg';
import { Loader } from '@/components/Loader/Loader';
import { useTypedDispatch, useTypedSelector } from '@/hooks';
import { selectCouponTab, setCouponTab } from '@/store';
import type { CouponTabState } from '@/types';
import { CouponBetsHistory } from '../CouponBetsHistory/CouponBetsHistory';
import { CouponBody } from '../CouponBody/CouponBody';
import { CouponPreviewBody } from '../CouponPreviewBody/CouponPreviewBody';
import { CouponTabs } from '../CouponTabs/CouponTabs';
import type { CouponWrapperProps } from './CouponWrapper.types';
import styles from './CouponWrapper.module.scss';
const CouponWrapperBase = forwardRef<HTMLDivElement, CouponWrapperProps>(({
  className,
  onArrowLeftClick,
  swipeableHandlers = {},
  isShowLeftArrow,
  variant = 'default'
}, ref) => {
  const dispatch = useTypedDispatch();
  const tabState = useTypedSelector(selectCouponTab);
  const isLimitLoaded = useTypedSelector(selectCouponLimitsLoaded);
  const isFreebetsLoaded = useTypedSelector(selectFreebetsIsFirstLoaded);
  const isBalanceFirstLoaded = useTypedSelector(selectUserBalanceIsFirstLoaded);
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const iaSuggestedAmountsLoaded = useTypedSelector(selectCouponSuggestedAmountsLoaded);
  const openedCoupons = useTypedSelector(selectorCouponHistoryOpenCouponsV2);
  const currency = useTypedSelector(selectUserCurrency);
  const locale = useTypedSelector(selectUserLocale);
  const selectedBets = useTypedSelector(selectCouponSelectedBetsV2);
  const createdCoupons = useTypedSelector(selectCreatedCouponsV2);
  const prevCreatedCouponsLength = usePrevious(createdCoupons.length);
  const prevSelectedBetsLength = usePrevious(selectedBets.length);
  const isDataLoading = isAuth && (!isLimitLoaded || !isFreebetsLoaded || !isBalanceFirstLoaded || !iaSuggestedAmountsLoaded);
  useEffect(() => {
    if (currency) {
      dispatch(loadCouponHistoryV2Request());
    }
  }, [dispatch, currency, locale]);
  useEffect(() => {
    if (createdCoupons.length > 0 && prevCreatedCouponsLength === 0 && createdCoupons[0].status === 'OPENED') {
      dispatch(setCouponTab({
        ...tabState,
        active: 'bets'
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdCoupons]);
  useEffect(() => {
    if (selectedBets.length > 0 && selectedBets.length !== prevSelectedBetsLength) {
      dispatch(setCouponTab({
        ...tabState,
        active: 'coupon'
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBets]);
  const handleSetCouponTab = (couponTabState: CouponTabState) => {
    dispatch(setCouponTab(couponTabState));
    if (couponTabState.active === 'coupon') {
      dispatch(removeCreatedOpenedCoupons());
    }
  };
  return <section className={cn(styles.root, styles[`${variant}Variant`], className)}>
        <div className={styles.wrapper} ref={ref}>
          <div className={styles.tabsWrapper} {...swipeableHandlers}>
            {isShowLeftArrow && <div className={styles.arrowWrapper}>
                <ArrowLeft width={16} height={16} className={styles.arrowLeft} onClick={onArrowLeftClick} />
              </div>}
            <CouponTabs tabState={tabState} setTabState={handleSetCouponTab} betsCount={openedCoupons.length} className={styles.couponTabs} />
          </div>
          <div className={cn(styles.contentWrapper, {
        [styles.rightTabSelected]: tabState.active === 'bets'
      })}>
            <div className={styles.content}>
              {isDataLoading && tabState.active === 'coupon' && <div className={styles.loader}>
                  <Loader />
                </div>}
              {!isDataLoading && tabState.active === 'coupon' && (variant === 'default' ? <CouponBody /> : <CouponPreviewBody />)}
              {tabState.active === 'bets' && <CouponBetsHistory />}
            </div>
          </div>
        </div>
      </section>;
});
CouponWrapperBase.displayName = 'CouponWrapperBase';
const CouponWrapper = memo(CouponWrapperBase);
CouponWrapper.displayName = 'CouponWrapper';
export { CouponWrapper };